import { TranslateService } from '@ngx-translate/core';
import { Signal } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface GreylistAwareControl extends AbstractControl {
  _isGreylistedDomain?: boolean;
}

export function greylistEmailValidator(greylistedDomains: Signal<string[]>): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const email = control.value;
    if (!email) {
      (control as GreylistAwareControl)._isGreylistedDomain = false;
      return null;
    }

    const domain = email.split('@')[1]?.toLowerCase();
    const isGreylisted = domain && greylistedDomains().includes(domain);

    (control as GreylistAwareControl)._isGreylistedDomain = !!isGreylisted;

    return null;
  };
}

export function domainAlreadyInGreylistValidator(greylistedDomains: Signal<string[]>): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const domain = control.value?.trim()?.toLowerCase();

    if (!domain) return null;

    const isDuplicate = greylistedDomains().some((d) => d.toLowerCase().trim() === domain);

    return isDuplicate ? { domainAlreadyInGreylist: true } : null;
  };
}

export function calculateLatinLettersPercent(text: string | string[]): number {
  // Handle both single string and array of strings
  const combinedText = Array.isArray(text) ? text.filter(Boolean).join(' ') : text;

  if (!combinedText || typeof combinedText !== 'string') return 0;

  // Remove whitespace and common punctuation from the character count
  const cleanedValue = combinedText.replace(/[\s\p{Punctuation}]/gu, '');
  const totalChars = cleanedValue.length;
  if (totalChars === 0) return 0;

  // Unicode-aware match for Latin script (includes accented characters)
  const latinCharMatches = cleanedValue.match(/[\p{Script=Latin}\p{Number}]/gu) || [];
  const latinCount = latinCharMatches.length;

  return latinCount / totalChars;
}

export const latinLetterThresholdValidator = (thresholdPercent = 0.8): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'string') return null;

    const latinPercent = calculateLatinLettersPercent(value);
    if (latinPercent === 0) return null;

    return latinPercent >= thresholdPercent
      ? null
      : { notEnoughLatin: { percent: Math.round(latinPercent * 100) } };
  };
};

export const eLearningCodeValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value || typeof value !== 'string') {
      return null; // don’t validate empty — use Validators.required separately if needed
    }

    const trimmed = value.trim();
    const isValid = /^[A-Za-z0-9]{10}$/.test(trimmed);

    return isValid ? null : { invalidELearningCode: { value: trimmed } };
  };
};

export const urlValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value || typeof value !== 'string') {
      return null; // don’t validate empty — use Validators.required separately if needed
    }

    const trimmed = value.trim();

    // Match: starts with http(s):// or www., has at least two dots in hostname
    const regex = /^(https?:\/\/|www\.)[^/\s]+\.[^/\s]+\.[^/\s]+(\/[^\s]*)?$/i;

    const isValid = regex.test(trimmed);

    return isValid ? null : { invalidUrl: { value: trimmed } };
  };
};

export const passwordComplexityValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    const hasLetter = /[A-Za-z]/.test(value);
    const hasDigit = /\d/.test(value);

    const errors: ValidationErrors = {};
    if (!hasLetter) errors['missingLetter'] = true;
    if (!hasDigit) errors['missingDigit'] = true;

    return Object.keys(errors).length ? errors : null;
  };
};

export const confirmPasswordMatchesValidator = (matchTo: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent;
    if (!parent) return null;

    const targetValue = parent.get(matchTo)?.value;
    const currentValue = control.value;

    if (!targetValue || !currentValue) return null;

    return targetValue === currentValue ? null : { passwordMismatch: true };
  };
};

export const coordinateValidator = (isLatitude: boolean): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'string') return null;

    const num = parseFloat(value);
    const regex = /^-?\d{1,3}\.\d{6}$/;

    if (!regex.test(value)) return { invalidCoordinateFormat: true };

    const inRange = isLatitude ? num >= -90 && num <= 90 : num >= -180 && num <= 180;

    return inRange ? null : { coordinateOutOfRange: true };
  };
};

export const didValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || typeof value !== 'string') return null;

    const trimmed = value.trim();
    // DID format: did:method:identifier
    const didPattern = /^did:[a-z0-9]+:[a-zA-Z0-9._:%-]+$/;

    return didPattern.test(trimmed) ? null : { invalidDid: { value: trimmed } };
  };
};

export function getErrorMessage(
  control: AbstractControl | null,
  translate: TranslateService,
): string {
  if (!control || !control.errors) {
    return '';
  }

  if (control.errors['required']) {
    return translate.instant('this-field-is-required');
  }

  if (control.errors['email']) {
    return translate.instant('invalid-email');
  }

  if (control.errors['facilityNameTaken']) {
    return translate.instant('existing-facility');
  }

  if (control.errors['greylistedDomain']) {
    return translate.instant('personal-email');
  }

  if (control.errors['domainAlreadyInGreylist']) {
    return translate.instant('domain-already-in-greylist');
  }

  if (control.errors['notEnoughLatin']) {
    return translate.instant('use-latin-letters-a-z');
  }

  if (control.errors['missingLetter']) {
    return translate.instant('password-missing-letter');
  }

  if (control.errors['missingDigit']) {
    return translate.instant('password-missing-digit');
  }

  if (control.errors['passwordMismatch']) {
    return translate.instant('passwords-do-not-match');
  }

  if (control.errors['minlength']) {
    const requiredLength = control.errors['minlength'].requiredLength;
    return translate.instant('minlength-error', { length: requiredLength });
  }

  if (control.errors['passwordMismatch']) {
    return translate.instant('passwords-do-not-match');
  }

  if (control.errors['invalidCoordinateFormat']) {
    return translate.instant('invalid-coordinate-format');
  }

  if (control.errors['coordinateOutOfRange']) {
    return translate.instant('coordinate-out-of-range');
  }

  if (control.errors['invalidELearningCode']) {
    return translate.instant('invalid-e-learning-code');
  }

  if (control.errors['invalidUrl']) {
    return translate.instant('invalid-url');
  }

  if (control.errors['invalidDid']) {
    return translate.instant('invalid-did-format');
  }

  return translate.instant('invalid-input');
}
